import { useState } from "react";
import { useModal } from "react-modal-hook";
import { SUBMISSION_TYPE_MODULE } from "common/constants";
import { useDeferredPromise } from "../../../../../../hooks/useDeferredPromise";
import {
  ButtonSection,
  Container,
  ContentSection,
  HeaderSection,
  PrimaryButtons,
} from "../../../../../Common/__styles__/Modal";
import { Button } from "../../../../../Common/Buttons/Button";
import Modal from "../../../../../Common/Modal";
import { Body, Title } from "../../../../../Common/Typography";
import { ALL_MODULES } from "./list";

export function useDeleteModuleModal() {
  const { defer, deferRef } = useDeferredPromise<"cancel" | "confirm">();
  const [moduleRequiringField, setModuleRequiringField] =
    useState<SUBMISSION_TYPE_MODULE>();

  const [_showModal, hideModal] = useModal(() => {
    function onCancel() {
      deferRef.current?.resolve("cancel");
      hideModal();
    }

    return (
      <Modal onRequestClose={onCancel}>
        <DeleteModuleModal
          onCancel={onCancel}
          onSubmit={() => {
            deferRef.current?.resolve("confirm");
            hideModal();
          }}
          moduleRequiringField={moduleRequiringField!}
        />
      </Modal>
    );
  }, [moduleRequiringField]);

  function showModal(moduleRequiringField: SUBMISSION_TYPE_MODULE) {
    setModuleRequiringField(moduleRequiringField);
    _showModal();
    return defer().promise;
  }

  return [showModal, hideModal] as const;
}

type DeleteModuleModalProps = {
  onSubmit: () => void;
  onCancel: () => void;
  moduleRequiringField: SUBMISSION_TYPE_MODULE;
};

function DeleteModuleModal({
  onCancel,
  onSubmit,
  moduleRequiringField,
}: DeleteModuleModalProps) {
  const module = ALL_MODULES.find(
    module => module.type === moduleRequiringField,
  );
  const moduleName = module?.label ?? moduleRequiringField;

  return (
    <Container>
      <HeaderSection>
        <Title size="large" type="semiBold">
          Delete Module
        </Title>
      </HeaderSection>
      <ContentSection>
        <Body size="default" type="regular">
          This field is required for functionality of the {moduleName} module.
          To delete this field, you must delete the module and all of its
          associated fields.
        </Body>

        <Body size="default" type="emphasis">
          This action cannot be undone. Are you sure you want to continue?
        </Body>
      </ContentSection>
      <ButtonSection>
        <PrimaryButtons>
          <Button
            variant="secondary"
            onClick={onCancel}
            size="medium"
            title="Cancel"
          />
          <Button
            variant="critical"
            onClick={onSubmit}
            size="medium"
            title="Delete module and associated fields"
          />
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
}
